import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Save, Send, ChevronDown, ChevronUp, Edit3, FolderPlus, Clock, Target, Search, X, Check } from 'lucide-react';
import { toast } from 'sonner';

function MultiUserSelect({ users, selected, onChange, testId }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = users.filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.department?.toLowerCase().includes(q) || u.designation?.toLowerCase().includes(q);
  });

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const selectedNames = selected.map(id => { const u = users.find(x => x._id === id); return u ? u.name : ''; }).filter(Boolean);

  return (
    <div ref={ref} className="relative">
      <div
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="min-h-[32px] w-full border border-slate-300 rounded-md px-2 py-1 bg-white cursor-pointer text-sm flex flex-wrap items-center gap-1 hover:border-slate-400"
      >
        {selectedNames.length > 0 ? selectedNames.map((n, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">
            {n}
            <button onClick={(e) => { e.stopPropagation(); toggle(selected[i]); }} className="text-blue-400 hover:text-blue-700"><X className="w-2.5 h-2.5" /></button>
          </span>
        )) : <span className="text-slate-400 text-xs">Search & select staff...</span>}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-full min-w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={`${testId}-search`}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name, email..."
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:border-slate-400 focus:outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.slice(0, 50).map(u => {
              const isSelected = selected.includes(u._id);
              return (
                <label key={u._id} className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggle(u._id)} className="accent-blue-600 w-3.5 h-3.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">
                      <span className="font-medium text-slate-800">{u.name}</span>
                      <span className="text-slate-400 ml-1">({u.email})</span>
                    </div>
                    {(u.designation || u.department) && (
                      <div className="truncate text-[10px] text-slate-400">
                        {[u.designation, u.department].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
            {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-3">No staff found</p>}
            {filtered.length > 50 && <p className="text-xs text-slate-400 text-center py-1">Type to narrow results...</p>}
          </div>
          <div className="p-2 border-t border-slate-100 flex justify-between items-center">
            <span className="text-[10px] text-slate-400">{selected.length} selected</span>
            <button onClick={() => setOpen(false)} className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-2 py-1">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function makeId() {
  return Math.random().toString(36).substr(2, 9);
}

const emptyDuty = () => ({
  id: makeId(), date: '', from_time: '', to_time: '', topics: '', presea_classes_hrs: 0, presea_practicals_hrs: 0,
  online_classes_hrs: 0, post_sea_classes_hrs: 0, total_hours: 0, days_absent: 0,
  remarks: '', status: 'InProgress'
});

const emptyResponsibility = (groupName = 'General') => ({
  id: makeId(), group_name: groupName, date: '', from_time: '', to_time: '', topics: '', description: '',
  time_spent_hrs: 0, planned_hrs: 0, deadline: '', closing_out_date: '',
  ssv_princi_comments: '', cic_comments: '', status: 'InProgress'
});

const emptyMarketing = () => ({
  id: makeId(), date: '', from_time: '', to_time: '', topics: '', description: '', hours: 0, deadline: '',
  ssv_comments: '', csa_comments: '', status: 'InProgress'
});

function calcHoursFromTime(from_time, to_time) {
  if (!from_time || !to_time) return null;
  const [fh, fm] = from_time.split(':').map(Number);
  const [th, tm] = to_time.split(':').map(Number);
  const diff = (th * 60 + tm) - (fh * 60 + fm);
  return diff > 0 ? Math.round(diff / 30) * 0.5 : null;
}

// A row is "blank" if the user never typed anything meaningful into it — these are
// filtered out before saving so empty rows never pollute reports/analytics.
function isBlankDuty(d) {
  return !d.topics?.trim() && !d.remarks?.trim() && !(parseFloat(d.total_hours) || 0) && !(parseInt(d.days_absent) || 0);
}
function isBlankResponsibility(r) {
  return !r.topics?.trim() && !r.description?.trim() && !(parseFloat(r.time_spent_hrs) || 0) && !(parseFloat(r.planned_hrs) || 0);
}
function isBlankMarketing(m) {
  return !m.topics?.trim() && !m.description?.trim() && !(parseFloat(m.hours) || 0);
}

export default function ReportFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrincipal = user?.role === 'PrincipalSSV';
  const today = new Date().toISOString().split('T')[0];
  const [reportDate, setReportDate] = useState(today);
  const [duties, setDuties] = useState([emptyDuty()]);
  const [responsibilities, setResponsibilities] = useState([]);
  const [marketings, setMarketings] = useState([emptyMarketing()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({ academics: true, responsibilities: true, marketing: true });
  const [editingGroupName, setEditingGroupName] = useState(null);
  const [tempGroupName, setTempGroupName] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [showTimeTracking, setShowTimeTracking] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Load users for task assignment
  useEffect(() => {
    async function loadUsers() {
      try {
        const { data } = await api.getUsers();
        setAllUsers(data);
      } catch {}
    }
    if (['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role)) {
      loadUsers();
    }
  }, [user?.role]);

  // Auto-load saved responsibility groups on mount
  useEffect(() => {
    async function loadSavedGroups() {
      try {
        const { data: savedGroups } = await api.getMyResponsibilityGroups();
        if (savedGroups && savedGroups.length > 0) {
          const rows = savedGroups.map(g => emptyResponsibility(g));
          setResponsibilities(rows);
        } else {
          setResponsibilities([emptyResponsibility('General')]);
        }
      } catch {
        setResponsibilities([emptyResponsibility('General')]);
      } finally {
        setLoadingGroups(false);
      }
    }
    loadSavedGroups();
  }, []);
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateDuty = (index, field, value) => {
    setDuties(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (['presea_classes_hrs', 'presea_practicals_hrs', 'online_classes_hrs', 'post_sea_classes_hrs'].includes(field)) {
        const d = next[index];
        d.total_hours = (parseFloat(d.presea_classes_hrs) || 0) + (parseFloat(d.presea_practicals_hrs) || 0) +
          (parseFloat(d.online_classes_hrs) || 0) + (parseFloat(d.post_sea_classes_hrs) || 0);
      }
      // Auto-calc from time if both set
      if (['from_time', 'to_time'].includes(field)) {
        const d = next[index];
        const timeHrs = calcHoursFromTime(d.from_time, d.to_time);
        if (timeHrs !== null) d.total_hours = timeHrs;
      }
      return next;
    });
  };

  // Group responsibilities by group_name
  const getResponsibilityGroups = () => {
    const groups = [];
    const seen = new Set();
    responsibilities.forEach(r => {
      if (!seen.has(r.group_name)) {
        seen.add(r.group_name);
        groups.push(r.group_name);
      }
    });
    return groups;
  };

  const getGroupRows = (groupName) => {
    return responsibilities.filter(r => r.group_name === groupName);
  };

  const addResponsibilityGroup = () => {
    const existingNames = getResponsibilityGroups();
    let newName = 'New Responsibility';
    let counter = 1;
    while (existingNames.includes(newName)) {
      newName = `New Responsibility ${counter++}`;
    }
    setResponsibilities(prev => [...prev, emptyResponsibility(newName)]);
  };

  const renameGroup = (oldName, newName) => {
    if (!newName.trim() || (newName.trim() !== oldName && getResponsibilityGroups().includes(newName.trim()))) return;
    setResponsibilities(prev => prev.map(r =>
      r.group_name === oldName ? { ...r, group_name: newName.trim() } : r
    ));
    setEditingGroupName(null);
  };

  const deleteGroup = (groupName) => {
    const groups = getResponsibilityGroups();
    if (groups.length <= 1) return;
    setResponsibilities(prev => prev.filter(r => r.group_name !== groupName));
  };

  const addRowToGroup = (groupName) => {
    setResponsibilities(prev => [...prev, emptyResponsibility(groupName)]);
  };

  const updateResponsibility = (id, field, value) => {
    setResponsibilities(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeResponsibility = (id, groupName) => {
    setResponsibilities(prev => prev.filter(r => r.id !== id));
  };

  const assignTask = async (index) => {
    const t = tasks[index];
    if (!t.title?.trim()) { toast.error('Enter a task title'); return; }
    if (!t.assigned_to_ids?.length) { toast.error('Select at least one assignee'); return; }
    if (!t.deadline) { toast.error('Set a deadline'); return; }
    setTasks(prev => { const n = [...prev]; n[index] = { ...n[index], assigning: true }; return n; });
    let ok = 0;
    for (const assigneeId of t.assigned_to_ids) {
      try {
        await api.createTask({
          title: t.title.trim(),
          description: t.description || '',
          assigned_to: assigneeId,
          deadline: t.deadline,
          priority: t.priority || 'Medium',
          duration: t.duration ? parseFloat(t.duration) : null,
          duration_unit: t.duration_unit || 'hours',
        });
        ok++;
      } catch (e) {
        toast.error(`Failed to assign to one staff: ${e.response?.data?.detail || 'error'}`);
      }
    }
    setTasks(prev => { const n = [...prev]; n[index] = { ...n[index], assigning: false, assigned: ok > 0 }; return n; });
    if (ok > 0) toast.success(`Task assigned to ${ok} staff — they have been notified`);
  };

  const handleSave = async (submit = false) => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        report_date: reportDate,
        week_from: null,
        week_to: null,
        regular_duties: duties.filter(d => !isBlankDuty(d)).map(d => ({
          ...d,
          presea_classes_hrs: parseFloat(d.presea_classes_hrs) || 0,
          presea_practicals_hrs: parseFloat(d.presea_practicals_hrs) || 0,
          online_classes_hrs: parseFloat(d.online_classes_hrs) || 0,
          post_sea_classes_hrs: parseFloat(d.post_sea_classes_hrs) || 0,
          days_absent: parseInt(d.days_absent) || 0,
        })),
        responsibilities: responsibilities.filter(r => !isBlankResponsibility(r)).map(r => ({
          ...r,
          time_spent_hrs: parseFloat(r.time_spent_hrs) || 0,
          planned_hrs: parseFloat(r.planned_hrs) || 0,
        })),
        course_marketings: marketings.filter(m => !isBlankMarketing(m)),
      };
      const { data } = await api.createReport(payload);
      if (submit) {
        await api.submitReport(data._id);
      }
      // Submit tasks (one per assignee) — skip rows already assigned via the Assign button
      const validTasks = tasks.filter(t => !t.assigned && t.title.trim() && t.assigned_to_ids?.length > 0 && t.deadline);
      for (const t of validTasks) {
        for (const assigneeId of t.assigned_to_ids) {
          try {
            await api.createTask({
              title: t.title.trim(),
              description: t.description || '',
              assigned_to: assigneeId,
              deadline: t.deadline,
              priority: t.priority || 'Medium',
              duration: t.duration ? parseFloat(t.duration) : null,
              duration_unit: t.duration_unit || 'hours',
            });
          } catch {}
        }
      }
      navigate('/reports');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = ['InProgress', 'Completed', 'Overdue'];
  const statusColors = { InProgress: 'bg-amber-100 text-amber-800', Completed: 'bg-emerald-100 text-emerald-800', Overdue: 'bg-red-100 text-red-800' };
  const responsibilityGroups = getResponsibilityGroups();

  // Daily totals across all sections (duties + responsibilities + marketing)
  const dutyHoursTotal = duties.reduce((s, d) => s + (parseFloat(d.total_hours) || 0), 0);
  const respHoursTotal = responsibilities.reduce((s, r) => s + (parseFloat(r.time_spent_hrs) || 0), 0);
  const mktgHoursTotal = marketings.reduce((s, m) => s + (parseFloat(m.hours) || 0), 0);
  const dailyTotalHours = dutyHoursTotal + respHoursTotal + mktgHoursTotal;

  return (
    <div data-testid="report-form-page" className="space-y-6 animate-fade-in max-w-[1400px]">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">New Report</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Create Daily Report
        </h1>
      </div>

      {error && (
        <div data-testid="report-form-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Daily Total Hours Summary (sticky) */}
      <div data-testid="daily-total-hours" className="bg-gradient-to-r from-slate-900 to-slate-700 rounded-lg p-4 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300">Daily Total Hours</p>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="daily-total-value">{dailyTotalHours.toFixed(1)}<span className="text-base text-slate-300 ml-1">hrs</span></p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-center">
            <p className="text-slate-300 uppercase tracking-wider text-[9px] font-bold">Academics</p>
            <p className="font-bold text-base" data-testid="duty-hours-total">{dutyHoursTotal.toFixed(1)}</p>
          </div>
          <div className="w-px h-8 bg-slate-500" />
          <div className="text-center">
            <p className="text-slate-300 uppercase tracking-wider text-[9px] font-bold">Responsibilities</p>
            <p className="font-bold text-base" data-testid="resp-hours-total">{respHoursTotal.toFixed(1)}</p>
          </div>
          <div className="w-px h-8 bg-slate-500" />
          <div className="text-center">
            <p className="text-slate-300 uppercase tracking-wider text-[9px] font-bold">Initiatives</p>
            <p className="font-bold text-base" data-testid="mktg-hours-total">{mktgHoursTotal.toFixed(1)}</p>
          </div>
        </div>
      </div>

      {/* Date Selection */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-5">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="max-w-xs">
                <Label className="block text-sm font-semibold text-slate-700 mb-1.5">Report Date</Label>
                <Input data-testid="report-date-input" type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="border-slate-300" />
              </div>
              <button
                data-testid="mark-leave-btn"
                onClick={async () => {
                  if (!reportDate) return;
                  if (!window.confirm(`Mark ${reportDate} as leave/absent?`)) return;
                  try {
                    await api.markLeave(reportDate, 'Leave');
                    alert(`Leave marked for ${reportDate}`);
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to mark leave');
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
              >
                Mark as Absent/Leave
              </button>
            </div>
          {/* Time Tracking Toggle */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
            <button
              data-testid="toggle-time-tracking"
              onClick={() => setShowTimeTracking(!showTimeTracking)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                showTimeTracking ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              {showTimeTracking ? 'Time Tracking: ON' : 'Enable Time Tracking (Optional)'}
            </button>
            {showTimeTracking && <span className="text-[10px] text-slate-400">From/To time columns visible across all sections. Hours auto-calculated.</span>}
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Academics */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('academics')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">1</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Academics / Regular Duties</CardTitle>
              <span className="text-xs text-slate-400 ml-2">{duties.length} row(s)</span>
            </div>
            {expandedSections.academics ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.academics && (
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    {['Date', ...(showTimeTracking ? ['From', 'To'] : []), 'Topics', 'Presea Cls', 'Presea Prac', 'Online Cls', 'Post Sea', 'Total Hrs', 'Days Absent', 'Remarks', 'Status', ''].map(h => (
                      <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {duties.map((d, i) => (
                    <tr key={d.id} data-testid={`duty-row-${i}`} className="border-b border-slate-200">
                      <td className="border border-slate-200 p-1"><Input type="date" value={d.date} onChange={e => updateDuty(i, 'date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                      {showTimeTracking && (
                        <>
                          <td className="border border-slate-200 p-1"><Input type="time" value={d.from_time} onChange={e => updateDuty(i, 'from_time', e.target.value)} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                          <td className="border border-slate-200 p-1"><Input type="time" value={d.to_time} onChange={e => updateDuty(i, 'to_time', e.target.value)} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                        </>
                      )}
                      <td className="border border-slate-200 p-1"><Input value={d.topics} onChange={e => updateDuty(i, 'topics', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[120px]" placeholder="Topics" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.presea_classes_hrs} onChange={e => updateDuty(i, 'presea_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.presea_practicals_hrs} onChange={e => updateDuty(i, 'presea_practicals_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.online_classes_hrs} onChange={e => updateDuty(i, 'online_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.post_sea_classes_hrs} onChange={e => updateDuty(i, 'post_sea_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1 text-center font-bold text-slate-700">{d.total_hours}</td>
                      <td className="border border-slate-200 p-1"><Input type="number" value={d.days_absent} onChange={e => updateDuty(i, 'days_absent', e.target.value)} className="h-8 text-xs border-0 shadow-none w-14" /></td>
                      <td className="border border-slate-200 p-1"><Input value={d.remarks} onChange={e => updateDuty(i, 'remarks', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[100px]" placeholder="Remarks" /></td>
                      <td className="border border-slate-200 p-1">
                        <select value={d.status} onChange={e => updateDuty(i, 'status', e.target.value)} className={`h-8 text-xs rounded px-1 ${statusColors[d.status]}`}>
                          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="border border-slate-200 p-1">
                        <button data-testid={`remove-duty-${i}`} onClick={() => setDuties(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button data-testid="add-duty-row" onClick={() => setDuties(prev => [...prev, emptyDuty()])}
              className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors bg-transparent border border-dashed border-slate-300 hover:border-slate-400 rounded-md px-3 py-2 w-full justify-center">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          </CardContent>
        )}
      </Card>

      {/* Section 2: Responsibilities (Grouped) */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('responsibilities')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-slate-900 flex items-center justify-center text-white text-xs font-bold">2</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Responsibilities</CardTitle>
              <span className="text-xs text-slate-400 ml-2">{responsibilityGroups.length} group(s), {responsibilities.length} row(s)</span>
            </div>
            {expandedSections.responsibilities ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.responsibilities && (
          <CardContent className="pt-4 space-y-5">
            {responsibilityGroups.map((groupName) => {
              const groupRows = getGroupRows(groupName);
              const isEditing = editingGroupName === groupName;
              return (
                <div key={groupName} data-testid={`resp-group-${groupName}`} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Group Header */}
                  <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400" />
                      {isEditing ? (
                        <input
                          data-testid={`edit-group-name-input`}
                          type="text"
                          value={tempGroupName}
                          onChange={(e) => setTempGroupName(e.target.value)}
                          onBlur={() => renameGroup(groupName, tempGroupName)}
                          onKeyDown={(e) => { if (e.key === 'Enter') renameGroup(groupName, tempGroupName); if (e.key === 'Escape') setEditingGroupName(null); }}
                          autoFocus
                          className="text-sm font-bold text-slate-800 bg-white border border-slate-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-900 w-64"
                        />
                      ) : (
                        <span className="text-sm font-bold text-slate-800">{groupName}</span>
                      )}
                      {!isEditing && (
                        <button
                          data-testid={`rename-group-${groupName}`}
                          onClick={(e) => { e.stopPropagation(); setEditingGroupName(groupName); setTempGroupName(groupName); }}
                          className="text-slate-400 hover:text-slate-600 p-0.5"
                          title="Rename responsibility"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <span className="text-xs text-slate-400">({groupRows.length} entries)</span>
                    </div>
                    {isPrincipal && responsibilityGroups.length > 1 && (
                      <button
                        data-testid={`delete-group-${groupName}`}
                        onClick={() => deleteGroup(groupName)}
                        className="text-red-400 hover:text-red-600 p-1 text-xs font-medium flex items-center gap-1"
                        title="Remove this responsibility group"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Group Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-50/50">
                          {['Date', ...(showTimeTracking ? ['From', 'To'] : []), 'Topics', 'Description', 'Time Spent', 'Planned', 'Deadline', 'Closing Date', 'Status', ''].map(h => (
                            <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groupRows.map((r, i) => (
                          <tr key={r.id} data-testid={`resp-row-${r.id}`} className="border-b border-slate-100">
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.date} onChange={e => updateResponsibility(r.id, 'date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            {showTimeTracking && (
                              <>
                                <td className="border-r border-slate-100 p-1"><Input type="time" value={r.from_time} onChange={e => { updateResponsibility(r.id, 'from_time', e.target.value); const hrs = calcHoursFromTime(e.target.value, r.to_time); if (hrs !== null) updateResponsibility(r.id, 'time_spent_hrs', hrs); }} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                                <td className="border-r border-slate-100 p-1"><Input type="time" value={r.to_time} onChange={e => { updateResponsibility(r.id, 'to_time', e.target.value); const hrs = calcHoursFromTime(r.from_time, e.target.value); if (hrs !== null) updateResponsibility(r.id, 'time_spent_hrs', hrs); }} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                              </>
                            )}
                            <td className="border-r border-slate-100 p-1"><Input value={r.topics} onChange={e => updateResponsibility(r.id, 'topics', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[120px]" placeholder="Topics" /></td>
                            <td className="border-r border-slate-100 p-1"><Input value={r.description} onChange={e => updateResponsibility(r.id, 'description', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[150px]" placeholder="Description" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="number" step="0.5" min="0" value={r.time_spent_hrs} onChange={e => updateResponsibility(r.id, 'time_spent_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="number" step="0.5" min="0" value={r.planned_hrs} onChange={e => updateResponsibility(r.id, 'planned_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.deadline} onChange={e => updateResponsibility(r.id, 'deadline', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.closing_out_date} onChange={e => updateResponsibility(r.id, 'closing_out_date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            <td className="border-r border-slate-100 p-1">
                              <select value={r.status} onChange={e => updateResponsibility(r.id, 'status', e.target.value)} className={`h-8 text-xs rounded px-1 ${statusColors[r.status]}`}>
                                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td className="p-1">
                              <button data-testid={`remove-resp-${r.id}`} onClick={() => removeResponsibility(r.id, groupName)} className="text-red-400 hover:text-red-600 p-1">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Add Row to Group */}
                  <button
                    data-testid={`add-row-to-${groupName}`}
                    onClick={() => addRowToGroup(groupName)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors px-4 py-2.5 w-full border-t border-dashed border-slate-200 hover:bg-slate-50"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Entry
                  </button>
                </div>
              );
            })}

            {/* Add New Responsibility Group */}
            <button
              data-testid="add-responsibility-group"
              onClick={addResponsibilityGroup}
              className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors bg-blue-50 hover:bg-blue-100 border border-dashed border-blue-300 hover:border-blue-400 rounded-lg px-4 py-3 w-full justify-center"
            >
              <FolderPlus className="w-4 h-4" /> Add New Responsibility
            </button>
          </CardContent>
        )}
      </Card>

      {/* Section 3: Initiatives */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('marketing')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">3</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Initiatives</CardTitle>
              <span className="text-xs text-slate-400 ml-1">(Optional)</span>
              <span className="text-xs text-slate-400 ml-1">{marketings.length} row(s)</span>
            </div>
            {expandedSections.marketing ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.marketing && (
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    {['Date', ...(showTimeTracking ? ['From', 'To'] : []), 'Topics', 'Description', 'Hours', 'Deadline', 'Status', ''].map(h => (
                      <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {marketings.map((m, i) => (
                    <tr key={m.id} data-testid={`mktg-row-${i}`} className="border-b border-slate-200">
                      <td className="border border-slate-200 p-1"><Input type="date" value={m.date} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], date: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none" /></td>
                      {showTimeTracking && (
                        <>
                          <td className="border border-slate-200 p-1"><Input type="time" value={m.from_time} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], from_time: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                          <td className="border border-slate-200 p-1"><Input type="time" value={m.to_time} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], to_time: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none w-20" /></td>
                        </>
                      )}
                      <td className="border border-slate-200 p-1"><Input value={m.topics} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], topics: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none min-w-[120px]" placeholder="Topics" /></td>
                      <td className="border border-slate-200 p-1"><Input value={m.description} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], description: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none min-w-[150px]" placeholder="Description" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={m.hours} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], hours: parseFloat(e.target.value) || 0 }; return n; })} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="date" value={m.deadline} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], deadline: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none" /></td>
                      <td className="border border-slate-200 p-1">
                        <select value={m.status} onChange={e => setMarketings(prev => { const n = [...prev]; n[i] = { ...n[i], status: e.target.value }; return n; })} className={`h-8 text-xs rounded px-1 ${statusColors[m.status]}`}>
                          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="border border-slate-200 p-1">
                        <button data-testid={`remove-mktg-${i}`} onClick={() => setMarketings(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button data-testid="add-mktg-row" onClick={() => setMarketings(prev => [...prev, emptyMarketing()])}
              className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors bg-transparent border border-dashed border-slate-300 hover:border-slate-400 rounded-md px-3 py-2 w-full justify-center">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          </CardContent>
        )}
      </Card>

      {/* Section 4: Task Assignment */}
      <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('tasks')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded bg-orange-500 flex items-center justify-center text-white text-xs font-bold">4</div>
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Task Assignment</CardTitle>
                <span className="text-xs text-slate-400 ml-1">{tasks.length} task(s)</span>
              </div>
              {expandedSections.tasks ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </CardHeader>
          {expandedSections.tasks && (
            <CardContent className="pt-4 space-y-3">
              {tasks.map((t, i) => (
                <div key={t._localId} data-testid={`task-row-${i}`} className={`border rounded-lg p-3 space-y-2 ${t.assigned ? 'border-green-300 bg-green-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">Task Title</Label>
                      <Input data-testid={`task-title-${i}`} value={t.title} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], title: e.target.value }; return n; })} className="h-8 text-sm border-slate-300" placeholder="Task title..." />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">Assign To</Label>
                      <MultiUserSelect
                        users={allUsers}
                        selected={t.assigned_to_ids || []}
                        onChange={(ids) => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], assigned_to_ids: ids }; return n; })}
                        testId={`task-assignee-${i}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-slate-400">Description</Label>
                    <Input value={t.description} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], description: e.target.value }; return n; })} className="h-8 text-sm border-slate-300" placeholder="Task description..." />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">Deadline</Label>
                      <Input data-testid={`task-deadline-${i}`} type="date" value={t.deadline} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], deadline: e.target.value }; return n; })} className="h-8 text-sm border-slate-300" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">Duration</Label>
                      <div className="flex gap-1">
                        <Input data-testid={`task-duration-${i}`} type="number" min="0" step="0.5" value={t.duration ?? ''} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], duration: e.target.value }; return n; })} className="h-8 text-sm border-slate-300" placeholder="0" />
                        <select data-testid={`task-duration-unit-${i}`} value={t.duration_unit || 'hours'} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], duration_unit: e.target.value }; return n; })} className="h-8 border border-slate-300 rounded-md px-1 text-xs">
                          <option value="hours">hrs</option>
                          <option value="days">days</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">Priority</Label>
                      <select value={t.priority} onChange={e => setTasks(prev => { const n = [...prev]; n[i] = { ...n[i], priority: e.target.value }; return n; })} className="w-full h-8 border border-slate-300 rounded-md px-2 text-sm">
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button data-testid={`remove-task-${i}`} onClick={() => setTasks(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end pt-1">
                    {t.assigned ? (
                      <span data-testid={`task-assigned-badge-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-3 py-1.5 rounded-md">
                        <Check className="w-3.5 h-3.5" /> Assigned & notified
                      </span>
                    ) : (
                      <Button data-testid={`assign-task-btn-${i}`} onClick={() => assignTask(i)} disabled={t.assigning} className="h-8 bg-orange-500 hover:bg-orange-600 text-white text-xs">
                        <Target className="w-3.5 h-3.5 mr-1.5" /> {t.assigning ? 'Assigning...' : 'Assign'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <button data-testid="add-task-row" onClick={() => setTasks(prev => [...prev, { _localId: makeId(), title: '', description: '', assigned_to_ids: [], deadline: '', priority: 'Medium', duration: '', duration_unit: 'hours' }])}
                className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors bg-transparent border border-dashed border-orange-300 hover:border-orange-400 rounded-md px-3 py-2 w-full justify-center">
                <Target className="w-3.5 h-3.5" /> Assign New Task
              </button>
            </CardContent>
          )}
        </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:justify-end pb-8">
        <Button data-testid="cancel-report-btn" variant="ghost" onClick={() => navigate('/reports')} className="text-slate-600 hover:bg-slate-100">Cancel</Button>
        <Button data-testid="save-draft-btn" onClick={() => handleSave(false)} disabled={saving} className="bg-slate-200 text-slate-800 hover:bg-slate-300">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : 'Save as Draft'}
        </Button>
        <Button data-testid="save-submit-btn" onClick={() => handleSave(true)} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
          <Send className="w-4 h-4 mr-1.5" /> {saving ? 'Submitting...' : 'Save & Submit'}
        </Button>
      </div>
    </div>
  );
}
